import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { HydrantFeatureCollection } from "./hydrants.ts";
import teichSvg from "./assets/teich.svg?raw";

// Freie OSM-Vektor-Style-Quelle (OpenFreeMap Liberty). Keine Keys nötig.
const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

// Osdorf (24251), Schleswig-Holstein – Zentrum ca. 54.430°N / 10.018°E
// (Centroid der OSM-Hydranten; nur Fallback, eingeloggt wird auf die Daten gezoomt).
const DEFAULT_CENTER: [number, number] = [10.018, 54.43];
const DEFAULT_ZOOM = 13;

// Status-Ampel — Farbwerte CVD-geprüft (dataviz-Validator: alle Checks bestanden).
// Grau für "noch nie geprüft" ist bewusst neutral (ohne Chroma).
const STATUS_COLORS: Record<string, string> = {
  ok: "#1f8a4c",
  warn: "#b87a00",
  bad: "#c2201f",
  unknown: "#7c7c7c",
};
const DELETED_COLOR = "#7c7c7c";

// Löschteiche/Entnahmestellen: immer DIN-4066-Blau (aus dem Schild gesampelt),
// unabhängig vom Prüfstatus — Wasser ist da oder nicht, das altert nicht.
const POND_BLUE = "#0451a1";

// Form kodiert den Hydrantentyp — unabhängig von der Statusfarbe lesbar.
type Shape = "circle" | "square" | "triangle" | "triangle-down" | "diamond" | "hexagon";
const TYPE_SHAPES: Array<[type: string, shape: Shape]> = [
  ["pillar", "circle"],        // Überflurhydrant
  ["underground", "square"],   // Unterflurhydrant (eckige Straßenkappe)
  ["pipe", "triangle"],        // Rohrhydrant
  ["wall", "triangle-down"],   // Wandhydrant
  ["pond", "diamond"],         // Löschteich (Icon: DIN-4066-Schild)
  ["suction", "triangle"],     // Sauganschluss (immer blau, wie Teiche)
  ["other", "hexagon"],        // Sonstiges
];

export function createMap(container: HTMLElement): maplibregl.Map {
  const map = new maplibregl.Map({
    container,
    style: STYLE_URL,
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    attributionControl: { compact: true },
  });
  map.addControl(new maplibregl.NavigationControl(), "top-right");
  map.addControl(
    new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
    }),
    "top-right",
  );
  map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }));
  mountLegend(container);
  return map;
}

function shapePath(ctx: CanvasRenderingContext2D, shape: Shape, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  switch (shape) {
    case "circle":
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      break;
    case "square":
      ctx.rect(cx - r * 0.88, cy - r * 0.88, r * 1.76, r * 1.76);
      break;
    case "triangle":
      ctx.moveTo(cx, cy - r * 1.15);
      ctx.lineTo(cx + r * 1.05, cy + r * 0.85);
      ctx.lineTo(cx - r * 1.05, cy + r * 0.85);
      ctx.closePath();
      break;
    case "triangle-down":
      ctx.moveTo(cx, cy + r * 1.15);
      ctx.lineTo(cx + r * 1.05, cy - r * 0.85);
      ctx.lineTo(cx - r * 1.05, cy - r * 0.85);
      ctx.closePath();
      break;
    case "diamond":
      ctx.moveTo(cx, cy - r * 1.15);
      ctx.lineTo(cx + r * 0.95, cy);
      ctx.lineTo(cx, cy + r * 1.15);
      ctx.lineTo(cx - r * 0.95, cy);
      ctx.closePath();
      break;
    case "hexagon":
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
  }
}

// Marker-Icon zeichnen: gefüllt = aktiv, hohl = in OSM gelöscht.
function makeIcon(shape: Shape, color: string, hollow: boolean): ImageData {
  const size = 40;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const cx = size / 2;
  const cy = size / 2;
  const r = 13;

  // Weißer Halo, damit die Marker auf jedem Kartenhintergrund lesbar bleiben.
  shapePath(ctx, shape, cx, cy, r);
  ctx.lineWidth = 7;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
  ctx.stroke();

  shapePath(ctx, shape, cx, cy, r);
  if (hollow) {
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = color;
    ctx.stroke();
  } else {
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#1a1a1a";
    ctx.stroke();
  }
  return ctx.getImageData(0, 0, size, size);
}

// Teich-Schild (DIN 4066, quadratischer Rahmen + Tropfen) aus dem SVG rastern.
async function makeSignIcon(color: string): Promise<ImageData> {
  const size = 40;
  const svg = teichSvg.replace('fill="#000000"', `fill="${color}"`);
  const img = new Image();
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  await img.decode();
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  // Weiße Schildfläche hinter dem transparenten SVG-Inneren.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(5, 5, size - 10, size - 10);
  ctx.drawImage(img, 4, 4, size - 8, size - 8);
  return ctx.getImageData(0, 0, size, size);
}

let iconsReady: Promise<void> | null = null;

function ensureIcons(map: maplibregl.Map): Promise<void> {
  // Einmalig pro Karte bauen; parallele Aufrufe teilen sich dasselbe Promise
  // (doppeltes addImage mit gleichem Namen würde werfen).
  iconsReady ??= buildIcons(map);
  return iconsReady;
}

async function buildIcons(map: maplibregl.Map): Promise<void> {
  const pondIcon = await makeSignIcon(POND_BLUE);
  const pondDeleted = await makeSignIcon(DELETED_COLOR);
  for (const [type, shape] of TYPE_SHAPES) {
    for (const [status, color] of Object.entries(STATUS_COLORS)) {
      const name = `hyd-${type}-${status}`;
      if (map.hasImage(name)) continue;
      // Wasserentnahmestellen sind statusunabhängig blau: Teiche als Schild,
      // Sauganschlüsse als blaues Dreieck.
      const icon =
        type === "pond"
          ? pondIcon
          : type === "suction"
            ? makeIcon(shape, POND_BLUE, false)
            : makeIcon(shape, color, false);
      map.addImage(name, icon, { pixelRatio: 2 });
    }
    const delName = `hyd-${type}-deleted`;
    if (!map.hasImage(delName)) {
      map.addImage(delName, type === "pond" ? pondDeleted : makeIcon(shape, DELETED_COLOR, true), {
        pixelRatio: 2,
      });
    }
  }
}

export async function addHydrantLayer(map: maplibregl.Map, features: HydrantFeatureCollection): Promise<void> {
  const source = map.getSource("hydrants") as maplibregl.GeoJSONSource | undefined;
  if (source) {
    source.setData(features);
    return;
  }

  await ensureIcons(map);
  // Nach dem await erneut prüfen — ein paralleler Aufruf könnte schneller gewesen sein.
  if (map.getSource("hydrants")) {
    (map.getSource("hydrants") as maplibregl.GeoJSONSource).setData(features);
    return;
  }
  map.addSource("hydrants", { type: "geojson", data: features });

  map.addLayer({
    id: "hydrants-symbol",
    type: "symbol",
    source: "hydrants",
    layout: {
      "icon-image": [
        "concat",
        "hyd-",
        ["get", "type"],
        "-",
        ["case", ["get", "deleted"], "deleted", ["get", "status"]],
      ],
      "icon-size": ["interpolate", ["linear"], ["zoom"], 11, 0.5, 15, 0.75, 18, 1.1],
      "icon-allow-overlap": true,
    },
  });

  // Nennweite (DN) als Beschriftung, erst ab Zoom 15, kollisionsvermeidend.
  map.addLayer({
    id: "hydrants-dn",
    type: "symbol",
    source: "hydrants",
    minzoom: 15,
    layout: {
      "text-field": ["get", "dn"],
      "text-font": ["Noto Sans Regular"],
      "text-size": 11,
      "text-anchor": "top",
      "text-offset": [0, 1.0],
      "text-optional": true,
    },
    paint: {
      "text-color": "#333333",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.2,
    },
  });

  map.on("mouseenter", "hydrants-symbol", () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "hydrants-symbol", () => {
    map.getCanvas().style.cursor = "";
  });
}

// Gemeindegrenze als dezente gestrichelte Linie, unterhalb der Hydranten-Marker.
export function addBoundaryLayer(map: maplibregl.Map, boundaries: GeoJSON.FeatureCollection): void {
  const source = map.getSource("boundaries") as maplibregl.GeoJSONSource | undefined;
  if (source) {
    source.setData(boundaries);
    return;
  }

  map.addSource("boundaries", { type: "geojson", data: boundaries });
  const beforeId = map.getLayer("hydrants-symbol") ? "hydrants-symbol" : undefined;
  map.addLayer(
    {
      id: "boundary-line",
      type: "line",
      source: "boundaries",
      paint: {
        "line-color": "#444444",
        "line-width": 1.8,
        "line-dasharray": [3, 2],
        "line-opacity": 0.9,
      },
    },
    beforeId,
  );
}

// Kartenausschnitt auf die geladenen Hydranten einpassen.
export function fitToHydrants(map: maplibregl.Map, features: HydrantFeatureCollection): void {
  if (features.features.length === 0) return;
  const bounds = new maplibregl.LngLatBounds();
  for (const f of features.features) {
    bounds.extend(f.geometry.coordinates as [number, number]);
  }
  map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 0 });
}

export function onHydrantClick(
  map: maplibregl.Map,
  handler: (hydrantId: string) => void,
): void {
  map.on("click", "hydrants-symbol", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const id = f.properties?.id as string | undefined;
    if (id) handler(id);
  });
}

// Kleine SVG-Vorschau eines Markers für die Legende (gleiche Formen wie auf der Karte).
function legendSwatch(shape: Shape, color: string, hollow: boolean): string {
  const fill = hollow ? "#fff" : color;
  const stroke = hollow ? color : "#1a1a1a";
  const sw = hollow ? 2 : 1;
  const shapes: Record<Shape, string> = {
    circle: `<circle cx="8" cy="8" r="6" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    square: `<rect x="2.5" y="2.5" width="11" height="11" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    triangle: `<polygon points="8,1.5 14.5,13.5 1.5,13.5" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    "triangle-down": `<polygon points="8,14.5 14.5,2.5 1.5,2.5" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    diamond: `<polygon points="8,1.5 14,8 8,14.5 2,8" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    hexagon: `<polygon points="12.8,4.8 12.8,11.2 8,14 3.2,11.2 3.2,4.8 8,2" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
  };
  return `<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">${shapes[shape]}</svg>`;
}

// Das Teich-Schild als Mini-SVG für die Legende (gleiche Quelle wie das Karten-Icon).
function teichSwatch(): string {
  return teichSvg
    .replace(/<\?xml[^>]*\?>/, "")
    .replace(/<!DOCTYPE[^>]*>/, "")
    .replace('fill="#000000"', `fill="${POND_BLUE}"`)
    .replace(/width="[^"]*"/, 'width="16"')
    .replace(/height="[^"]*"/, 'height="16"');
}

function mountLegend(container: HTMLElement): void {
  const el = document.createElement("details");
  el.id = "map-legend";
  el.open = window.matchMedia("(min-width: 720px)").matches;
  el.innerHTML = `
    <summary>Legende</summary>
    <div class="legend-group">
      <strong>Status (letzte Prüfung)</strong>
      <span>${legendSwatch("circle", STATUS_COLORS.ok, false)} OK, jünger als 12 Monate</span>
      <span>${legendSwatch("circle", STATUS_COLORS.warn, false)} Prüfung fällig / zugestellt</span>
      <span>${legendSwatch("circle", STATUS_COLORS.bad, false)} defekt / nicht auffindbar</span>
      <span>${legendSwatch("circle", STATUS_COLORS.unknown, false)} noch nie geprüft</span>
    </div>
    <div class="legend-group">
      <strong>Typ</strong>
      <span>${legendSwatch("circle", "#555", false)} Überflurhydrant</span>
      <span>${legendSwatch("square", "#555", false)} Unterflurhydrant</span>
      <span>${legendSwatch("triangle", "#555", false)} Rohrhydrant</span>
      <span>${legendSwatch("triangle-down", "#555", false)} Wandhydrant</span>
      <span>${teichSwatch()} Löschteich</span>
      <span>${legendSwatch("triangle", POND_BLUE, false)} Sauganschluss</span>
      <span>${legendSwatch("hexagon", "#555", false)} Sonstiges</span>
      <span>${legendSwatch("circle", DELETED_COLOR, true)} in OSM gelöscht</span>
      <span><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><line x1="1" y1="8" x2="15" y2="8" stroke="#444" stroke-width="2" stroke-dasharray="3 2"/></svg> Gemeindegrenze</span>
    </div>
  `;
  container.append(el);
}
